import { FC, memo } from 'react';
import classNames from 'classnames';
import { ChevronLeftOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/ChevronLeftOutlined';
import { ChevronRightOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/ChevronRightOutlined';
import { UnderlinedButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import styles from './Pagination.module.scss';

import { PAGINATION } from '../analytics/analyticsEvents';

import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useExperiencesContext } from '../experiences/Experiences';

import StatefulLink, { facetsFilter } from '../stateful-link/StatefulLink';

type PaginationProps = {
  pages?: number;
  current?: number;
  isFooter?: boolean;
  className?: string;
  hidePaginationAbove768px?: boolean;
};

/**
 * Create Page array
 * @param totalPages
 * @param activePage
 * @returns {number[]|Array}
 */
export const getPages = (totalPages: number, activePage: number) => {
  if (totalPages <= 7) {
    return [1, 2, 3, 4, 5, 6, 7].slice(0, totalPages);
  }

  const pages = [];
  let start = activePage - 2;
  let end = activePage + 2;

  if (start < 2) {
    const diff = 2 - start;
    end += diff;
    start += diff;
  }
  if (end > totalPages - 1) {
    const diff = end - (totalPages - 1);
    end -= diff;
    start -= diff;
  }

  const startEllipsis = activePage > 3;
  const endEllipsis = activePage < totalPages - 2;

  pages.push(1);
  pages.push(startEllipsis ? '...' : start);
  for (let i = start + 1; i <= end - 1; i += 1) {
    pages.push(i);
  }
  pages.push(endEllipsis ? '...' : end);
  pages.push(totalPages);

  return pages;
};

export const Pagination: FC<PaginationProps> = ({
  pages,
  current = 1,
  className = '',
  isFooter = false,
  hidePaginationAbove768px = false,
  ...rest
}) => {
  const totalPages = pages;
  const activePage = current;

  const { isTabletEnhancementsFlagOn, isSingleColumnLayout } =
    useExperiencesContext();

  const { pageType } = useAnalyticsData();

  const paginationEvent = useAnalytics(PAGINATION);

  const onSelect = (pageNum: number): void => {
    paginationEvent({
      pagetype: pageType,
      pageNum,
    });
  };

  if (totalPages < 2) {
    return null;
  }

  const paginationPages = getPages(totalPages, activePage);

  return (
    <nav
      className={classNames(
        styles['c-pagination'],
        className,
        `${
          isFooter
            ? styles['c-pagination--footer']
            : styles['c-pagination--header']
        }`,
        {
          [styles['c-pagination--tabletEnhancements']]:
            !isFooter && isTabletEnhancementsFlagOn,
          [styles['c-pagination--hidden']]: hidePaginationAbove768px,
          [styles['c-pagination--singleColumn']]: isSingleColumnLayout,
        }
      )}
      {...rest}
    >
      <ul
        className={classNames(styles['c-pagination__list'])}
        data-testid={isFooter ? 'pagination-footer' : 'pagination'}
      >
        {activePage > 1 && (
          <li
            className={classNames([
              styles['c-pagination__prev'],
              styles['c-pagination__item'],
            ])}
          >
            <StatefulLink
              query={{ page: activePage - 1 }}
              queryOpts={{ filter: facetsFilter() }}
              aria-label="Previous"
              onClick={() => onSelect(activePage - 1)}
              data-testid="prev-btn"
              className={classNames([
                styles['c-pagination__btn'],
                styles['c-pagination__prev-btn'],
              ])}
            >
              <ChevronLeftOutlined size="medium" />
              <UnderlinedButton
                tag="span"
                className={styles['c-pagination__prev-text']}
              >
                Previous
              </UnderlinedButton>
            </StatefulLink>
          </li>
        )}
        <li
          className={classNames([
            styles['c-pagination__pages'],
            styles['c-pagination__item'],
          ])}
        >
          {paginationPages.map((page, index) => {
            switch (page) {
              case '...':
                return (
                  <span
                    className={styles['c-pagination__ellipsis']}
                    key={index === 1 ? 'e1' : 'e2'}
                    data-testid="ellipsis"
                  >
                    ...
                  </span>
                );
              case activePage:
                return (
                  <span
                    key={page}
                    data-testid="page-active"
                    className={classNames([
                      styles['c-pagination__btn'],
                      styles['c-pagination__active'],
                      styles['c-pagination__active-underline'],
                    ])}
                  >
                    {page}
                  </span>
                );
              default:
                return (
                  <StatefulLink
                    query={{ page }}
                    queryOpts={{ filter: facetsFilter() }}
                    key={page}
                    onClick={() => onSelect(page)}
                    data-testid="page-btn"
                    className={classNames([
                      styles['c-pagination__btn'],
                      {
                        [styles['c-pagination__active']]: activePage === page,
                      },
                    ])}
                  >
                    {page}
                  </StatefulLink>
                );
            }
          })}
        </li>
        <li
          className={classNames([
            styles['c-pagination__next'],
            styles['c-pagination__item'],
          ])}
        >
          {activePage < totalPages && (
            <StatefulLink
              query={{ page: activePage + 1 }}
              queryOpts={{ filter: facetsFilter() }}
              role="button"
              aria-label="Next"
              className={classNames([
                styles['c-pagination__btn'],
                styles['c-pagination__next-btn'],
              ])}
              data-testid="next-btn"
              onClick={() => onSelect(activePage + 1)}
            >
              <UnderlinedButton
                tag="span"
                className={styles['c-pagination__next-text']}
              >
                Next
              </UnderlinedButton>
              <ChevronRightOutlined size="medium" />
            </StatefulLink>
          )}
        </li>
      </ul>
    </nav>
  );
};

export default memo(Pagination);
